/**
 * SchemaMarkup — Comprehensive JSON-LD structured data component.
 * Renders Service, LocalBusiness, FAQPage, and BreadcrumbList schemas
 * based on page type and current route. Injected via direct DOM manipulation.
 */
import { useSchemaDOM } from '@/hooks/useSchemaDOM';
import { SITE_URL } from '@/lib/seo';

type PageType = 'home' | 'service' | 'contact' | 'about' | 'faq' | 'industry' | 'blog' | 'location' | 'pricing' | 'compare';

interface FAQItem {
  question: string;
  answer: string;
}

interface SchemaMarkupProps {
  pageType: PageType;
  lang: string;
  pagePath: string;
  pageTitle: string;
  pageDescription: string;
  faqItems?: FAQItem[];
}


function buildBreadcrumbSchema(pagePath: string) {
  const segments = pagePath.replace(/\/$/, '').split('/').filter(Boolean);
  if (segments.length <= 1) return null;

  const items = segments.map((seg, i) => ({
    '@type': 'ListItem' as const,
    position: i + 1,
    name: seg.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    item: `${SITE_URL}/${segments.slice(0, i + 1).join('/')}`,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

function buildFAQPageSchema(faqItems: FAQItem[]) {
  if (!faqItems.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

const LOCAL_BUSINESS_PAGES = new Set(['contact', 'about', 'location', 'locations']);

export function SchemaMarkup({ pageType, lang, pagePath, pageTitle, pageDescription, faqItems }: SchemaMarkupProps) {
  // Dedup policy (audit 2026-06-12):
  // - BreadcrumbList → owned by BreadcrumbAutoGenerator (single source).
  // - Organization / LocalBusiness / WebSite → owned by EnterpriseSchemas (single source).
  // - Service → owned by per-page templates (BPOServiceTemplate etc.).
  // - FAQPage → owned by the page that actually shows the FAQ.
  // This component now only acts as a fallback for legacy pageTypes that don't
  // ship their own schemas, and never duplicates a global schema.
  const schemas: object[] = [];

  // FAQPage fallback ONLY if the page explicitly opts-in by passing faqItems
  // and isn't a service page (service templates inject their own FAQPage).
  const isServicePage = pageType === 'service' || /\/services\//.test(pagePath) || /\/services$/.test(pagePath);
  if (faqItems && faqItems.length > 0 && pageType === 'faq' && !isServicePage) {
    const faqSchema = buildFAQPageSchema(faqItems);
    if (faqSchema) schemas.push(faqSchema);
  }

  useSchemaDOM(schemas, `schema-markup-${pageType}`, [lang, pagePath, pageTitle, faqItems?.length]);

  return null;
}
